import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCaAuth } from "@/context/CaAuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import DatePickerField from "@/components/DatePickerField";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, LogOut, ChevronDown, Calendar, ScrollText, Flag, FileBarChart,
  Download, ArrowRight, CheckCircle2, AlertTriangle, XCircle, Lock,
} from "lucide-react";

const rupee = (v) => v == null ? "-" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function CaPortal() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { ca, pumps, loading, logout } = useCaAuth();
  const [selectedPumpId, setSelectedPumpId] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!loading && !ca) nav("/ca/login", { replace: true });
  }, [ca, loading, nav]);
  useEffect(() => {
    if (pumps.length && !selectedPumpId) setSelectedPumpId(pumps[0].pump_id);
  }, [pumps, selectedPumpId]);

  const pump = useMemo(() => pumps.find((p) => p.pump_id === selectedPumpId), [pumps, selectedPumpId]);

  if (loading) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">{t("ca.caportal.loading")}</div>;
  if (!ca) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-border bg-white/90 backdrop-blur dark:bg-slate-900/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-white shadow">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="font-bold">{t("ca.caportal.hisabx")} <span className="text-amber-500">CA</span></div>
              <div className="text-[11px] text-muted-foreground">{ca.name} · {ca.firm_name || "Independent"}</div>
            </div>
          </div>

          {/* Workspace switcher */}
          <div className="flex items-center gap-2">
            {pumps.length > 1 && (
              <div className="relative">
                <select
                  value={selectedPumpId || ""}
                  onChange={(e) => setSelectedPumpId(e.target.value)}
                  className="h-9 rounded-md border border-border bg-card pr-8 pl-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="ca-workspace-switcher"
                >
                  {pumps.map((p) => (
                    <option key={p.pump_id} value={p.pump_id}>{p.pump_name}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
              </div>
            )}
            {pumps.length === 1 && (
              <div className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold" data-testid="ca-single-pump">
                {pumps[0]?.pump_name}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={async () => {
              if (window.confirm("Sign out?")) { await logout(); nav("/ca/login"); }
            }} data-testid="ca-logout">
              <LogOut className="h-4 w-4" /> <span className="hidden md:inline">{t("common.logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      {!pump ? (
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <ShieldCheck className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
          <h2 className="text-lg font-bold">{t("ca.caportal.no_pumps_assigned_yet")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Ask any of your dealer clients to invite you from their HisabX dashboard.
          </p>
        </div>
      ) : !pump.ca_module_enabled ? (
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
          <h2 className="text-lg font-bold">CA module is disabled for {pump.pump_name}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Ask the dealer to enable the CA add-on from their Settings → CA Add-on page.
          </p>
        </div>
      ) : (
        <main className="mx-auto max-w-7xl px-4 py-5 md:px-6 md:py-6">
          {/* Pump banner */}
          <div className="mb-4 rounded-xl border-2 border-amber-500/40 bg-amber-50 p-3 dark:bg-amber-950/20" data-testid="ca-pump-header">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">{t("ca.caportal.auditing")}</div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">{pump.pump_name}</div>
                <div className="text-xs text-muted-foreground">{pump.omc || "—"} · {pump.pump_address || "No address"}</div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Access:
                {(pump.permissions || []).map((p) => (
                  <span key={p} className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-4 flex gap-1 overflow-x-auto border-b border-border">
            {[
              { key: "dashboard", label: "Dashboard", icon: Calendar },
              { key: "audit", label: "Audit trail", icon: ScrollText },
              { key: "flags", label: "Flags", icon: Flag },
              { key: "reports", label: "Reports", icon: FileBarChart },
              { key: "form3cd", label: "Form 3CD", icon: FileBarChart },
            ].map((t) => {
              const I = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={"flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition " +
                    (tab === t.key ? "border-amber-500 text-amber-700 dark:text-amber-300" : "border-transparent text-muted-foreground hover:text-foreground")}
                  data-testid={`ca-tab-${t.key}`}
                >
                  <I className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "dashboard" && <DashboardTab pumpId={pump.pump_id} />}
          {tab === "audit" && <AuditTab pumpId={pump.pump_id} />}
          {tab === "flags" && <FlagsTab pumpId={pump.pump_id} />}
          {tab === "reports" && <ReportsTab pumpId={pump.pump_id} tallyEnabled={pump.tally_export_enabled} />}
          {tab === "form3cd" && <Form3cdTab pumpId={pump.pump_id} />}
        </main>
      )}
    </div>
  );
}

// ================= DASHBOARD =================
function DashboardTab({ pumpId }) {
  const { t } = useTranslation();
  const [cal, setCal] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [flags, setFlags] = useState([]);

  useEffect(() => {
    api.get(`/ca/reports/compliance-calendar?pump_id=${pumpId}`).then((r) => setCal(r.data.items || [])).catch(() => {});
    api.get(`/ca/audit-trail/verify?pump_id=${pumpId}`).then((r) => setIntegrity(r.data)).catch(() => {});
    api.get(`/ca/flags?pump_id=${pumpId}&status=open`).then((r) => setFlags(r.data.flags || [])).catch(() => {});
  }, [pumpId]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Compliance calendar */}
      <Card className="border-border" data-testid="ca-calendar-card">
        <CardContent className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-bold uppercase tracking-widest">{t("ca.caportal.compliance_calendar")}</h3>
          </div>
          <div className="space-y-2">
            {cal.map((c) => {
              const Icon = c.status === "red" ? XCircle : c.status === "amber" ? AlertTriangle : CheckCircle2;
              const color = c.status === "red" ? "text-red-600" : c.status === "amber" ? "text-amber-600" : "text-emerald-600";
              return (
                <div key={c.code} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-3" data-testid={`cal-${c.code}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`h-4 w-4 ${color}`} />
                      <span className="font-semibold">{c.label}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{c.desc}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs font-mono">{c.due_date}</div>
                    <div className={`text-[10px] font-bold uppercase ${color}`}>
                      {c.days_until >= 0 ? `${c.days_until}d left` : `${Math.abs(c.days_until)}d overdue`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Right column: integrity + flags */}
      <div className="space-y-4">
        <Card className="border-border" data-testid="ca-integrity-card">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">{t("ca.caportal.audit_chain_integrity")}</h3>
            {integrity ? (
              <div className={"rounded-lg p-3 " + (integrity.ok ? "bg-emerald-50 dark:bg-emerald-950/20" : "bg-red-50 dark:bg-red-950/20")}>
                <div className="flex items-center gap-2">
                  {integrity.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                  <span className="font-bold">{integrity.message}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{integrity.total} events on record. SHA-256 hash chain verified.</div>
              </div>
            ) : <div className="text-sm text-muted-foreground">Checking…</div>}
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="ca-flags-summary">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest">{t("ca.caportal.open_flags")}</h3>
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-black text-white">{flags.length}</span>
            </div>
            {flags.length === 0 ? (
              <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/20">Nothing to flag. Books look clean 👍</div>
            ) : (
              <div className="space-y-1.5">
                {flags.slice(0, 5).map((f) => (
                  <div key={f.id} className="rounded-md border border-border bg-card p-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className={"rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase " +
                        (f.severity === "critical" ? "bg-red-100 text-red-700" : f.severity === "warn" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700")}>
                        {f.severity}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">{f.target_type}:{f.target_id.slice(0, 12)}</span>
                    </div>
                    <p className="mt-1 text-foreground/90">{f.note}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ================= AUDIT TRAIL =================
function AuditTab({ pumpId }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/ca/audit-trail?pump_id=${pumpId}&limit=200`)
      .then((r) => setRows(r.data.trail || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pumpId]);

  return (
    <Card className="border-border">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-widest">Immutable audit log ({rows.length})</h3>
          <div className="text-[10px] text-muted-foreground">SHA-256 hash chained · append-only</div>
        </div>
        {loading ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("ca.caportal.no_activity_yet")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b text-left uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="py-2">{t("ca.caportal.when")}</th>
                  <th>{t("ca.caportal.actor")}</th>
                  <th>{t("ca.caportal.action")}</th>
                  <th>{t("ca.caportal.entity")}</th>
                  <th>{t("ca.caportal.hash")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60" data-testid={`audit-row-${r.id}`}>
                    <td className="py-2 font-mono text-[10px]">{r.ts?.slice(0, 19).replace("T", " ")}</td>
                    <td>{r.actor_role} · <span className="font-mono text-[10px]">{(r.actor_id || "").slice(0, 12)}</span></td>
                    <td><span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold">{r.action}</span></td>
                    <td className="text-muted-foreground">{r.entity_type} · <span className="font-mono text-[10px]">{(r.entity_id || "").slice(0, 12)}</span></td>
                    <td className="font-mono text-[10px] text-muted-foreground">{r.hash?.slice(0, 12)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ================= FLAGS =================
function FlagsTab({ pumpId }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ target_type: "HISAB", target_id: "", severity: "warn", note: "" });
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/ca/flags?pump_id=${pumpId}`).then((r) => setRows(r.data.flags || [])).catch(() => {});
  useEffect(() => { load(); }, [pumpId]);

  const submit = async () => {
    if (!form.target_id.trim() || !form.note.trim()) return toast.error(t("ca.caportal.enter_target_id_and_note"));
    setPosting(true);
    try {
      await api.post(`/ca/flags?pump_id=${pumpId}`, form);
      toast.success(t("ca.caportal.flag_posted"));
      setForm({ target_type: "HISAB", target_id: "", severity: "warn", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
      <Card className="border-border">
        <CardContent className="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">Flags ({rows.length})</h3>
          {rows.length === 0 ? <div className="text-sm text-muted-foreground">{t("ca.caportal.no_flags_yet")}</div> : (
            <div className="space-y-2">
              {rows.map((f) => (
                <div key={f.id} className="rounded-lg border border-border bg-card p-3" data-testid={`flag-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " +
                          (f.severity === "critical" ? "bg-red-100 text-red-700" : f.severity === "warn" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700")}>
                          {f.severity}
                        </span>
                        <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase " +
                          (f.status === "open" ? "bg-orange-100 text-orange-800" : f.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500")}>
                          {f.status}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground">{f.target_type}:{f.target_id}</span>
                      </div>
                      <p className="mt-1.5 text-sm">{f.note}</p>
                    </div>
                    <div className="shrink-0 text-right text-[10px] text-muted-foreground">
                      {f.created_at?.slice(0, 10)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border h-fit">
        <CardContent className="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">{t("ca.caportal.post_a_new_flag")}</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">{t("ca.caportal.target_type")}</label>
              <select value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-2 text-sm" data-testid="flag-target-type">
                <option>{t("ca.caportal.hisab")}</option><option>{t("ca.caportal.cashbook")}</option><option>{t("ca.caportal.expense")}</option><option>{t("ca.caportal.credit")}</option><option>{t("ca.caportal.other")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">{t("ca.caportal.target_id")}</label>
              <Input value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} className="h-9 text-sm font-mono" placeholder="e.g. hi-abc123" data-testid="flag-target-id" />
            </div>
            <div>
              <label className="text-xs font-semibold">{t("ca.caportal.severity")}</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-2 text-sm" data-testid="flag-severity">
                <option value="info">{t("ca.caportal.info")}</option><option value="warn">{t("ca.caportal.warn")}</option><option value="critical">{t("ca.caportal.critical")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">{t("csa.note")}</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 min-h-[80px] w-full rounded-md border border-border bg-card p-2 text-sm" data-testid="flag-note" />
            </div>
            <Button onClick={submit} disabled={posting} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="flag-submit">
              {posting ? "Posting…" : "Post flag"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ================= REPORTS =================
function ReportsTab({ pumpId, tallyEnabled }) {
  const { t } = useTranslation();
  const monthNow = today().slice(0, 7);
  const [range, setRange] = useState({ from: today().slice(0, 8) + "01", to: today() });
  const [month, setMonth] = useState(monthNow);
  const [sales, setSales] = useState(null);
  const [gst, setGst] = useState(null);
  const [cash, setCash] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const loadAll = async () => {
    try {
      const [s, g, c] = await Promise.all([
        api.get(`/ca/reports/sales-daybook?pump_id=${pumpId}&date_from=${range.from}&date_to=${range.to}`),
        api.get(`/ca/reports/gst-split?pump_id=${pumpId}&month=${month}`),
        api.get(`/ca/reports/cash-payments-large?pump_id=${pumpId}&date_from=${range.from}&date_to=${range.to}`),
      ]);
      setSales(s.data); setGst(g.data); setCash(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, [pumpId, range.from, range.to, month]);

  const tallyUrl = `${backend}/api/ca/tally/sales-xml?pump_id=${pumpId}&date_from=${range.from}&date_to=${range.to}`;

  return (
    <div className="space-y-4">
      {/* Range picker */}
      <Card className="border-border">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("manager.from")}</label>
            <DatePickerField value={range.from} onChange={(iso) => setRange({ ...range, from: iso })} className="h-9 w-52" testId="rpt-date-from" ariaLabel="From date" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">To</label>
            <DatePickerField value={range.to} onChange={(iso) => setRange({ ...range, to: iso })} className="h-9 w-52" testId="rpt-date-to" ariaLabel="To date" min={range.from} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("ca.caportal.gst_month")}</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="rpt-month" />
          </div>
          {tallyEnabled ? (
            <a href={tallyUrl} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="tally-download">
              <Download className="h-3.5 w-3.5" /> Tally XML (Sales) <ArrowRight className="h-3 w-3" />
            </a>
          ) : (
            <div className="ml-auto text-[10px] text-muted-foreground" data-testid="tally-disabled-hint">
              💡 Ask the dealer to enable Tally export in their CA Add-on settings
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sales day-book */}
      <Card className="border-border" data-testid="report-sales-daybook">
        <CardContent className="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">Sales Day-Book · {range.from} → {range.to}</h3>
          {sales ? (
            <div>
              <div className="mb-3 flex flex-wrap gap-4 text-sm">
                <Stat label={t("ca.caportal.total_sales")} value={rupee(sales.grand_totals?.sales)} />
                <Stat label={t("csa.litres")} value={(sales.grand_totals?.litres || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} />
                <Stat label={t("csa.csaportal.cash")} value={rupee(sales.grand_totals?.cash)} />
                <Stat label={t("setup.online")} value={rupee(sales.grand_totals?.online)} />
                <Stat label={t("manager.kpi_credit")} value={rupee(sales.grand_totals?.credit)} />
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card">
                    <tr className="border-b text-left uppercase text-muted-foreground">
                      <th className="py-2">{t("manager.date")}</th><th>{t("csa.shift")}</th><th>{t("ca.caportal.salesman")}</th>
                      <th className="text-right">{t("csa.sales")}</th><th className="text-right">{t("csa.csaportal.cash")}</th><th className="text-right">{t("setup.online")}</th><th className="text-right">{t("manager.kpi_credit")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sales.days || []).flatMap((d) =>
                      (d.shifts || []).map((s) => (
                        <tr key={s.hisab_id} className="border-b border-border/50">
                          <td className="py-2 font-mono">{d.date}</td>
                          <td>{s.shift}</td>
                          <td>{s.csa_name}</td>
                          <td className="text-right font-mono">{rupee(s.sales)}</td>
                          <td className="text-right font-mono">{rupee(s.cash)}</td>
                          <td className="text-right font-mono">{rupee(s.online)}</td>
                          <td className="text-right font-mono">{rupee(s.credit)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
        </CardContent>
      </Card>

      {/* GST split */}
      <Card className="border-border" data-testid="report-gst-split">
        <CardContent className="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">GST Split · {month}</h3>
          {gst ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border-2 border-slate-400/40 bg-slate-50 p-3 dark:bg-slate-900/60">
                <div className="text-xs font-bold uppercase text-slate-500">Non-GST · {gst.non_gst_supplies.gstr1_table}</div>
                <div className="mt-1 text-xs text-muted-foreground">HSN {gst.non_gst_supplies.hsn}</div>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between"><span>{t("ca.caportal.petrol")}</span><span className="font-mono">{rupee(gst.non_gst_supplies.petrol.amount)}</span></div>
                  <div className="flex justify-between"><span>{t("ca.caportal.diesel")}</span><span className="font-mono">{rupee(gst.non_gst_supplies.diesel.amount)}</span></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>{t("csa.csaleaves.total")}</span><span className="font-mono">{rupee(gst.non_gst_supplies.total)}</span></div>
                </div>
              </div>
              <div className="rounded-lg border-2 border-amber-400/40 bg-amber-50 p-3 dark:bg-amber-950/20">
                <div className="text-xs font-bold uppercase text-amber-700">Taxable (18% GST)</div>
                <div className="mt-1 text-xs text-muted-foreground">HSN {gst.taxable_supplies.hsn}</div>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Lubricants ({gst.taxable_supplies.lubricants.count})</span><span className="font-mono">{rupee(gst.taxable_supplies.lubricants.amount)}</span></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>{t("csa.csaleaves.total")}</span><span className="font-mono">{rupee(gst.taxable_supplies.total)}</span></div>
                </div>
              </div>
            </div>
          ) : <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
        </CardContent>
      </Card>

      {/* Cash payments >10K + 269ST receipts */}
      <Card className="border-border" data-testid="report-cash-payments">
        <CardContent className="p-5">
          <h3 className="mb-1 text-sm font-bold uppercase tracking-widest">Form 3CD Clause 21 · Section 269ST</h3>
          <p className="mb-3 text-xs text-muted-foreground">Cash payments &gt; ₹10,000 (Section 40A(3)) and cash receipts &gt; ₹2,00,000 in a single day.</p>
          {cash ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="text-xs font-semibold text-red-700">Cash payments &gt; ₹10K ({cash.clause_21_count})</div>
                {(cash.clause_21_cash_payments_over_10k || []).map((p) => (
                  <div key={p.id} className="mt-1 rounded border border-border bg-card p-2 text-xs">
                    <div className="font-mono">{p.date} · <b>{rupee(p.amount)}</b></div>
                    <div className="text-muted-foreground">{p.type} · {p.note}</div>
                  </div>
                ))}
                {cash.clause_21_count === 0 && <div className="text-xs text-muted-foreground mt-1">{t("ca.caportal.none_clean")}</div>}
              </div>
              <div>
                <div className="text-xs font-semibold text-red-700">Cash receipts &gt; ₹2L ({cash.section_269st_count})</div>
                {(cash.section_269st_large_cash_receipts || []).map((p) => (
                  <div key={p.id} className="mt-1 rounded border border-red-300 bg-red-50 p-2 text-xs dark:bg-red-950/20">
                    <div className="font-mono">{p.date} · <b>{rupee(p.amount)}</b></div>
                    <div className="text-muted-foreground">{p.type} · {p.note}</div>
                    <div className="mt-0.5 text-[10px] font-bold text-red-700">⚠ Section 269ST — 100% penalty risk</div>
                  </div>
                ))}
                {cash.section_269st_count === 0 && <div className="text-xs text-muted-foreground mt-1">{t("ca.caportal.none_clean")}</div>}
              </div>
            </div>
          ) : <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-lg font-bold">{value}</div>
    </div>
  );
}

// ================= FORM 3CD =================
function Form3cdTab({ pumpId }) {
  const { t } = useTranslation();
  const [fy, setFy] = useState(() => {
    const today = new Date();
    const y = today.getFullYear();
    // FY: Apr-Mar. If we're in Jan-Mar, FY is (y-1)-(y%100).
    const isBeforeApr = today.getMonth() < 3;
    const startYear = isBeforeApr ? y - 1 : y;
    return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    setData(null); setErr(null);
    api.get(`/ca/reports/form-3cd?pump_id=${pumpId}&fy=${fy}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); }, [pumpId, fy]);

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `form-3cd-${pumpId}-${fy}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("ca.caportal.financial_year")}</label>
            <Input value={fy} onChange={(e) => setFy(e.target.value)} className="h-9 w-32 font-mono" placeholder="2025-26" data-testid="fy-input" />
          </div>
          <Button onClick={load} className="h-9" data-testid="fy-load">{t("ca.caportal.regenerate")}</Button>
          <Button variant="outline" onClick={downloadJson} disabled={!data} className="ml-auto h-9" data-testid="fy-download">
            <Download className="h-3.5 w-3.5" /> Download JSON
          </Button>
        </CardContent>
      </Card>

      {err && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{err}</div>}
      {!data && !err && <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
      {data && (
        <>
          <Card className="border-border">
            <CardContent className="p-5">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-widest">Turnover summary · {data.period?.from} → {data.period?.to}</h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Stat label={t("ca.caportal.total_sales")} value={rupee(data.turnover_summary?.total_sales)} />
                <Stat label={t("ca.caportal.non_gst_petrol_diesel")} value={rupee(data.turnover_summary?.non_gst_sales_petrol_diesel)} />
                <Stat label={t("ca.caportal.taxable_lubes_etc")} value={rupee(data.turnover_summary?.taxable_sales_lubes_etc)} />
                <Stat label={t("ca.caportal.audit_required")} value={data.turnover_summary?.tax_audit_required ? "YES" : "no"} />
              </div>
              {data.turnover_summary?.tax_audit_required && (
                <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/20">
                  ⚠ Turnover crossed ₹1 Cr threshold — Section 44AB tax audit required. Deadline 30 Sep.
                </div>
              )}
            </CardContent>
          </Card>

          {[
            ["clause_11_books_of_account", "Clause 11 — Books of account"],
            ["clause_14_stock_valuation", "Clause 14 — Stock valuation"],
            ["clause_17_method_of_accounting", "Clause 17 — Method of accounting"],
            ["clause_20_non_deductible", "Clause 20 — Non-deductible amounts"],
            ["clause_21_cash_payments_over_10k", "Clause 21 — Cash payments > ₹10,000 (Section 40A(3))"],
            ["clause_34_tds_compliance", "Clause 34 — TDS compliance"],
            ["clause_36_related_party", "Clause 36 — Related-party transactions"],
            ["clause_44_gst_breakup", "Clause 44 — GST breakup (Non-GST vs Taxable)"],
            ["section_269st_large_cash_receipts", "Section 269ST — Cash receipts > ₹2 L"],
          ].map(([key, title]) => (
            <ClauseCard key={key} title={title} data={data[key]} testid={`3cd-${key}`} />
          ))}
        </>
      )}
    </div>
  );
}

function ClauseCard({ title, data, testid }) {
  const { t } = useTranslation();
  const entries = data && typeof data === "object" ? Object.entries(data) : [];
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-widest text-amber-600">{title}</h4>
        {entries.length === 0 ? <div className="text-xs text-muted-foreground">{t("ca.caportal.no_data")}</div> : (
          <div className="space-y-1 text-xs">
            {entries.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[minmax(140px,240px)_1fr] gap-2 border-b border-border/40 py-1">
                <span className="font-semibold text-muted-foreground">{k}</span>
                <span className="font-mono">{formatValue(v)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatValue(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.length === 0 ? "—" : `${v.length} item${v.length > 1 ? "s" : ""}`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 200);
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
}
